import { Dialog as BaseDialog } from '@base-ui/react/dialog'
import { type VariantProps } from 'class-variance-authority'
import { use } from 'react'

import { PortalContainerContext } from '../../../lib/portal-container-context.ts'
import { cn } from '../../../lib/utils.ts'

import { popupVariants } from './dialog.variants.ts'

/**
 * Manages the open state and interaction of the dialog parts.
 *
 * @summary Dialog state root.
 */
function DialogRoot(props: DialogRoot.Props) {
  return <BaseDialog.Root {...props} />
}
namespace DialogRoot {
  export type Props = BaseDialog.Root.Props
}

function DialogTrigger(props: DialogTrigger.Props) {
  return <BaseDialog.Trigger data-slot="dialog-trigger" {...props} />
}
namespace DialogTrigger {
  export type Props = BaseDialog.Trigger.Props
}

function DialogPortal(props: DialogPortal.Props) {
  const { container, ...rest } = props
  const scopeContainer = use(PortalContainerContext)
  // Base UI renders nothing for `container={null}`, so an absent scope host must become `undefined`
  // to get its document.body fallback.
  return <BaseDialog.Portal data-slot="dialog-portal" container={container ?? scopeContainer ?? undefined} {...rest} />
}
namespace DialogPortal {
  export type Props = BaseDialog.Portal.Props
}

function DialogClose(props: DialogClose.Props) {
  return <BaseDialog.Close data-slot="dialog-close" {...props} />
}
namespace DialogClose {
  export type Props = BaseDialog.Close.Props
}

function DialogViewport(props: DialogViewport.Props) {
  return <BaseDialog.Viewport data-slot="dialog-viewport" {...props} />
}
namespace DialogViewport {
  export type Props = BaseDialog.Viewport.Props
}

/**
 * The styled dialog surface. Contains the dialog content and collapses to fullscreen below the `md` breakpoint.
 *
 * @summary Dialog content surface.
 */
function DialogPopup(props: DialogPopup.Props) {
  const { className, size = 'medium', ...rest } = props
  return <BaseDialog.Popup data-slot="dialog-popup" className={cn(popupVariants({ size, className }))} {...rest} />
}
namespace DialogPopup {
  type Variants = NonNullable<VariantProps<typeof popupVariants>>
  export type Props = BaseDialog.Popup.Props & {
    /** Max width on desktop. Auto-collapses to fullscreen below the `md` breakpoint. @default 'medium' */
    size?: NonNullable<Variants['size']> | undefined
  }
}

function DialogBackdrop(props: DialogBackdrop.Props) {
  const { className, blur = false, ...rest } = props
  return (
    <BaseDialog.Backdrop
      data-slot="dialog-backdrop"
      className={cn(
        `
          fixed inset-0 z-40 bg-black/50
          data-closed:animate-out data-closed:motion-exit data-closed:fade-out-0 data-closed:fill-mode-forwards
          data-open:animate-in data-open:motion-enter data-open:fade-in-0
        `,
        blur && 'backdrop-blur-md',
        className,
      )}
      {...rest}
    />
  )
}
namespace DialogBackdrop {
  export type Props = BaseDialog.Backdrop.Props & {
    /** Apply a 12 px backdrop blur to the page behind the dialog. @default false */
    blur?: boolean
  }
}

function DialogTitle(props: DialogTitle.Props) {
  const { className, ...rest } = props
  return (
    <BaseDialog.Title
      data-slot="dialog-title"
      className={cn('m-0 font-heading text-50 leading-50 font-600 tracking-10 text-content-base-main', className)}
      {...rest}
    />
  )
}
namespace DialogTitle {
  export type Props = BaseDialog.Title.Props
}

function DialogDescription(props: DialogDescription.Props) {
  const { className, ...rest } = props
  return (
    <BaseDialog.Description
      data-slot="dialog-description"
      className={cn('font-body text-20 leading-30 font-400 tracking-20 text-content-base-basic', className)}
      {...rest}
    />
  )
}
namespace DialogDescription {
  export type Props = BaseDialog.Description.Props
}

/**
 * `Dialog` mirrors `@base-ui/react/dialog` 1:1 — `Root` + `Trigger` + `Portal` + `Backdrop`
 * + `Popup` + `Viewport` + `Title` + `Description` + `Close` — and adds Orus token styling
 * + `data-slot` on each part. The layout parts that have no Base UI counterpart
 * (`Header`/`Body`/`Footer`/`CloseIconButton`) live in `patterns/dialog`.
 *
 * For a dialog with the standard layout, reach for `patterns/dialog` instead: it pre-wires
 * `Portal` + `Backdrop` + `Popup`, carries the layout parts, and covers controlled and uncontrolled
 * use with a `Trigger`. Use these primitives directly for detached triggers (`createHandle`) or any
 * shape the pattern does not cover, and provide your own layout and spacing inside `Popup`. The pattern's
 * `Header`, `Body` and `Footer` rely on its `Popup` to coordinate adjacent padding; reusing them in a
 * primitive `Popup` without those spacing rules doubles the padding between parts.
 *
 * - `Dialog.Portal` defaults its container to the nearest pharaoh-next scope
 *   (`PortalContainerContext`), falling back to `document.body`.
 * - `Dialog.Popup` auto-collapses to fullscreen below the `md` breakpoint. Override per-instance
 *   via `className` (e.g. `max-md:!h-auto max-md:!w-[calc(100%-2rem)] max-md:!rounded-30`) — this
 *   requires using `ui/dialog` directly, since `patterns/dialog`'s `Popup` forwards no `className`
 *   to the Popup.
 * - `Dialog.Backdrop` has an opt-in `blur` prop (default off — blur is expensive).
 *
 * @summary Composable modal-dialog primitive (Base UI namespace).
 */
const Dialog = {
  Root: DialogRoot,
  Trigger: DialogTrigger,
  Portal: DialogPortal,
  Close: DialogClose,
  Viewport: DialogViewport,
  Backdrop: DialogBackdrop,
  Popup: DialogPopup,
  Title: DialogTitle,
  Description: DialogDescription,
}

namespace Dialog {
  export namespace Root {
    export type Props = DialogRoot.Props
  }
  export namespace Trigger {
    export type Props = DialogTrigger.Props
  }
  export namespace Portal {
    export type Props = DialogPortal.Props
  }
  export namespace Close {
    export type Props = DialogClose.Props
  }
  export namespace Viewport {
    export type Props = DialogViewport.Props
  }
  export namespace Backdrop {
    export type Props = DialogBackdrop.Props
  }
  export namespace Popup {
    export type Props = DialogPopup.Props
  }
  export namespace Title {
    export type Props = DialogTitle.Props
  }
  export namespace Description {
    export type Props = DialogDescription.Props
  }
}

export { Dialog, popupVariants }
